import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Bell, BellOff, ChevronRight, Loader2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BrandLogo from '../components/brand/BrandLogo';
import { getBuyerId, syncBuyerPush, isPushDeliveryConfirmed } from '../lib/push';
import {
  POLL_MS, loadSeen, saveSeen, createChimePlayer, isStandalone,
} from '../lib/buyerNotifications';

/**
 * Buyer "My Sellers" home (/me) — Step 2 navigation layer + Step 4 buyer
 * notifications.
 *
 * Shows ONLY the sellers this buyer has connected to. A seller is connected
 * AUTOMATICALLY when the buyer opens that seller's catalogue link (handled by
 * CollectionView -> /buyer/connect). Buyers do NOT manually add sellers here.
 *
 * Each seller stays completely isolated: tapping one sets it as the ACTIVE
 * seller context and opens that seller's EXISTING public catalogue + chat
 * (/c/:id) — no parallel chat system, no cross-seller leakage.
 *
 * Step 4: a notification bell lists new-collection notifications from the
 * buyer's connected sellers (each tagged with its seller). Tapping one opens
 * ONLY that seller's catalogue/chat. A genuinely-new notification plays a
 * soft, soothing chime once (distinct from the seller ka-ching) and can raise
 * a background browser notification. Dedup via a persisted seen-id set.
 */

const ACTIVE_KEY = 'fabtexs.active_seller';

const readLS = (k) => { try { return localStorage.getItem(k); } catch (_e) { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch (_e) { /* private mode */ } };

export default function BuyerHome() {
  const nav = useNavigate();
  const base = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [activeSeller, setActiveSeller] = useState(() => readLS(ACTIVE_KEY));
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  // Shared resolver: localStorage -> cookie mirror -> minted. Previously this
  // read localStorage directly and a cleared/reinstalled PWA got `null`, which
  // disabled the poller entirely (blank bell, no chime, no alerts).
  const [buyerId] = useState(() => getBuyerId());

  // ── audio + dedup refs (soft buyer chime; never the seller ka-ching) ──────
  // Dedup set and chime come from the shared module so this bell and the
  // catalogue-screen alert layer can never diverge or double-alert.
  const seen = useRef(loadSeen());
  const primed = useRef(false);          // first poll only seeds the seen-set
  const chimer = useRef(createChimePlayer());

  const loadConnections = useCallback((bid) => {
    if (!bid) { setConnections([]); setLoading(false); return; }
    axios.get(`${base}/api/buyer/connections`, { params: { buyer_id: bid } })
      .then(({ data }) => setConnections(data.connections || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [base]);

  useEffect(() => { loadConnections(buyerId); }, [buyerId, loadConnections]);

  // Keep the web-push subscription alive. Silent on load (never prompts) —
  // this is what heals a subscription lost to a PWA reinstall, cleared
  // site-data, or a browser-side endpoint rotation. Without it the buyer had
  // no way back to a working closed-app notification.
  useEffect(() => {
    if (!buyerId) return;
    syncBuyerPush({ buyerId }).catch(() => {});
  }, [buyerId]);

  // Unlock audio + request notification permission on the buyer's FIRST
  // interaction (browser autoplay policy; no opt-in UI).
  useEffect(() => {
    const unlock = () => {
      chimer.current.unlock();
      // Requesting permission here is inside a real user gesture, so we also
      // complete the push subscribe in the same step — otherwise granting
      // permission produced a notification-capable buyer with no subscription.
      if ('Notification' in window && Notification.permission === 'default') {
        syncBuyerPush({ buyerId, requestPermission: true }).catch(() => {});
      } else if (isStandalone()) {
        syncBuyerPush({ buyerId }).catch(() => {});
      }
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId]);

  const chime = () => chimer.current.play();   // shared player (autoplay-safe)

  // Stand down ONLY when push is PROVEN to be delivering for this buyer; a
  // subscription object alone is not proof. Fails closed so the bell's own
  // banner keeps working whenever push delivery is unverified.
  const pushProven = useRef(false);

  const systemNotify = (n) => {
    try {
      if (pushProven.current) return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      const notification = new Notification(`🔔 ${n.seller_name || 'New collection'}`, {
        body: n.body || 'New collection is here. Tap to explore.',
        tag: n.id, icon: '/favicon.png',
      });
      notification.onclick = () => { window.focus(); openNotification(n); };
    } catch (_e) { /* noop */ }
  };

  // Poll the buyer notification feed. New unread items (not previously seen)
  // trigger ONE soft chime + a background browser notification. The first poll
  // only seeds the seen-set so a refresh never replays old alerts.
  useEffect(() => {
    if (!buyerId) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        pushProven.current = await isPushDeliveryConfirmed({ ownerKind: 'buyer', ownerId: buyerId });
        const { data } = await axios.get(`${base}/api/buyer/notifications`, { params: { buyer_id: buyerId } });
        if (!alive) return;
        const items = data.items || [];
        setNotifs(items);
        setUnread(data.unread_count || 0);
        const fresh = items.filter((n) => n.id && !n.is_read && !seen.current.has(n.id));
        if (primed.current && fresh.length > 0) {
          chime();
          fresh.slice(0, 3).forEach(systemNotify);
        }
        items.forEach((n) => n.id && seen.current.add(n.id));
        saveSeen(seen.current);
        primed.current = true;
      } catch (_e) { /* offline / transient — ignore */ }
    };
    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, base]);

  const openSeller = (c) => {
    if (!c.last_collection_id) { toast.error('No catalogue available for this seller yet.'); return; }
    writeLS(ACTIVE_KEY, c.seller_id);
    setActiveSeller(c.seller_id);
    nav(`/c/${c.last_collection_id}`);
  };

  // Tap a notification → mark read, set the active seller context, and open
  // ONLY that seller's catalogue/chat (the notification's own deep-link).
  const openNotification = (n) => {
    if (n.seller_id) { writeLS(ACTIVE_KEY, n.seller_id); setActiveSeller(n.seller_id); }
    if (n.id && buyerId) {
      axios.post(`${base}/api/buyer/notifications/${n.id}/read`, { buyer_id: buyerId }).catch(() => {});
    }
    setPanelOpen(false);
    nav(n.url || (n.short_code ? `/c/${n.short_code}` : '/me'));
  };

  const markAllRead = () => {
    if (!buyerId) return;
    axios.post(`${base}/api/buyer/notifications/read-all`, { buyer_id: buyerId }).catch(() => {});
    setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
  };

  return (
    <div className="min-h-screen bg-[#efe7dc]" data-testid="buyer-home">
      <header className="sticky top-0 z-10 bg-[#f7f2eb]/95 backdrop-blur-md border-b border-black/10 px-4 py-3 flex items-center gap-2">
        <BrandLogo className="h-7 w-auto" />
        <span className="text-[13px] font-semibold text-neutral-600 ml-auto">My Sellers</span>
        <button
          onClick={() => setPanelOpen((v) => !v)}
          className="relative p-2 -mr-1 rounded-full hover:bg-black/5"
          aria-label="Notifications"
          data-testid="buyer-notif-bell"
        >
          <Bell className="w-5 h-5 text-neutral-700" />
          {unread > 0 && (
            <span
              data-testid="buyer-notif-count"
              className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[#c0392b] text-white text-[10px] font-bold flex items-center justify-center"
            >{unread > 9 ? '9+' : unread}</span>
          )}
        </button>
      </header>

      {panelOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setPanelOpen(false)} />
          <div
            className="absolute right-3 top-14 z-30 w-[min(92vw,360px)] bg-white rounded-xl shadow-xl border border-black/10 overflow-hidden"
            data-testid="buyer-notif-panel"
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/5">
              <span className="text-[13px] font-semibold text-neutral-800">Notifications</span>
              <div className="flex items-center gap-2">
                {notifs.some((n) => !n.is_read) && (
                  <button onClick={markAllRead} className="text-[11px] font-semibold text-[#075E54]" data-testid="buyer-notif-readall">Mark all read</button>
                )}
                <button onClick={() => setPanelOpen(false)} aria-label="Close" className="p-1 rounded-full hover:bg-black/5"><X className="w-4 h-4 text-neutral-500" /></button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="px-4 py-10 text-center text-neutral-400 text-[12.5px]" data-testid="buyer-notif-empty">
                  No notifications yet
                </div>
              ) : (
                notifs.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openNotification(n)}
                    data-testid={`buyer-notif-${n.id}`}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-black/5 hover:bg-black/[0.03] ${n.is_read ? '' : 'bg-[#075E54]/[0.05]'}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#075E54]/10 flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-[#075E54]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-neutral-900 truncate">{n.seller_name || 'New collection'}</div>
                      <div className="text-[12px] text-neutral-600 leading-snug">{n.body || 'A new collection is here. Tap to explore.'}</div>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#075E54] mt-1.5 shrink-0" />}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      <main className="max-w-lg mx-auto p-4">
        <p className="text-[12.5px] text-neutral-500 mb-3">
          {connections.length > 0 ? `${connections.length} seller${connections.length === 1 ? '' : 's'} connected` : 'Sellers you connect with appear here'}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-400" data-testid="buyer-home-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : connections.length === 0 ? (
          <div className="text-center py-16 text-neutral-500" data-testid="buyer-home-empty">
            <Store className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-[14px] font-medium">No sellers yet</p>
            <p className="text-[12.5px] mt-1">Open a seller&apos;s catalogue link and they&apos;ll appear here automatically.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="buyer-connections-list">
            {connections.map((c) => {
              const isActive = activeSeller === c.seller_id;
              return (
                <button
                  key={c.seller_id}
                  data-testid={`buyer-seller-${c.seller_id}`}
                  onClick={() => openSeller(c)}
                  className={`w-full flex items-center gap-3 bg-white rounded-xl p-3.5 shadow-sm hover:shadow-md transition-shadow text-left ${isActive ? 'ring-2 ring-[#075E54]' : ''}`}
                >
                  <div className="w-11 h-11 rounded-full bg-[#075E54]/10 flex items-center justify-center shrink-0">
                    <Store className="w-5 h-5 text-[#075E54]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[14px] font-semibold text-neutral-900 truncate">{c.business_name}</div>
                      {isActive && (
                        <span data-testid={`active-badge-${c.seller_id}`} className="text-[10px] font-bold uppercase tracking-wide text-[#075E54] bg-[#075E54]/10 rounded-full px-2 py-0.5 shrink-0">Active</span>
                      )}
                    </div>
                    <div className="text-[12px] text-neutral-500 flex items-center gap-1.5">
                      {c.city ? <span>{c.city}</span> : null}
                      {c.collection_notifications
                        ? <span className="inline-flex items-center gap-1 text-[#075E54]"><Bell className="w-3 h-3" /> Notifications on</span>
                        : <span className="inline-flex items-center gap-1 text-neutral-400"><BellOff className="w-3 h-3" /> No alerts</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-neutral-300 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
